/* eslint-disable react-refresh/only-export-components -- the provider and its
   `useConsent` reader are one unit; splitting them only adds an import hop. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  activeConsent,
  applyConsent,
  blocksConnect as computeBlocksConnect,
  effectiveConsent,
  needsConsentChoice,
  saveConsent,
  type ConsentDecisions,
  type ConsentRecord,
} from "@/engine/diagnostics/consent";
import {
  fetchRegionRule,
  GLOBAL_DEFAULT,
  type RegionRule,
} from "@/engine/support/consentRegions";
import type { InitialConsent } from "@/providers/initialConsent";

/**
 * The React seam over `src/lib/consent.ts`. One provider near the app root runs
 * the region lookup once and applies the undecided-visitor default; the entry
 * screen reads `blocksConnect`, the consent surface reads `showSurface` and the
 * current decisions. Nothing here decides policy — that is the lib's job.
 *
 * It starts from what the server already knew (#531). Where the server could
 * read the country, the rule is in hand on the first render and the surface is
 * server-rendered rather than appearing after hydration; where it could not,
 * `initial.rule` is null and the browser's lookup decides as it always did.
 */

export interface ConsentContextValue {
  /** The decision in force for each optional category right now. */
  decisions: ConsentDecisions;
  record: ConsentRecord | null;
  /** True once the region lookup has settled. */
  regionResolved: boolean;
  needsChoice: boolean;
  requiresChoice: boolean;
  /** The visitor must choose before Connect works. */
  blocksConnect: boolean;
  /** The surface should be on screen. */
  showSurface: boolean;
  save: (next: ConsentDecisions) => void;
  reopen: () => void;
  dismiss: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /** What the render was started with. Absent in an isolated mount. */
  initial?: InitialConsent;
}) {
  const [record, setRecord] = useState<ConsentRecord | null>(
    () => initial?.record ?? activeConsent(),
  );
  const [rule, setRule] = useState<RegionRule | null>(initial?.rule ?? null);
  const [reopened, setReopened] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const serverRule = initial?.rule ?? null;
  useEffect(() => {
    // The server read the country off the request; there is nothing to look up
    // and nothing to wait for.
    if (serverRule) {
      if (!activeConsent()) applyConsent(effectiveConsent(null, serverRule));
      return;
    }
    let cancelled = false;
    void fetchRegionRule().then((resolved) => {
      if (cancelled) return;
      setRule(resolved);
      // An undecided visitor gets the region default applied now, so PostHog
      // stays off wherever the region denies analytics.
      if (!activeConsent()) applyConsent(effectiveConsent(null, resolved));
    });
    return () => {
      cancelled = true;
    };
  }, [serverRule]);

  const save = useCallback((next: ConsentDecisions) => {
    saveConsent(next);
    setRecord(activeConsent());
    setReopened(false);
    setDismissed(false);
  }, []);
  const reopen = useCallback(() => setReopened(true), []);
  const dismiss = useCallback(() => setDismissed(true), []);

  const value = useMemo<ConsentContextValue>(() => {
    const needsChoice = needsConsentChoice(record);
    const requiresChoice = rule?.requiresChoice ?? false;
    return {
      decisions: effectiveConsent(record, rule ?? GLOBAL_DEFAULT),
      record,
      regionResolved: rule !== null,
      needsChoice,
      requiresChoice,
      blocksConnect: rule !== null && computeBlocksConnect(record, rule),
      // Only a region that demands a prior choice gets the surface unasked.
      // Elsewhere the persistent "Cookie settings" entry point is the whole
      // obligation, so the start screen stays uncovered.
      showSurface: reopened || (requiresChoice && needsChoice && !dismissed),
      save,
      reopen,
      dismiss,
    };
  }, [record, rule, reopened, dismissed, save, reopen, dismiss]);

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

/**
 * What a consumer sees with no `ConsentProvider` above it: nothing to show,
 * nothing blocked. The real app always has the provider; this keeps isolated
 * renders (stories, focused tests) from needing it just to mount a consumer.
 */
const DETACHED: ConsentContextValue = {
  decisions: { analytics: "granted", marketing: "granted" },
  record: null,
  regionResolved: false,
  needsChoice: false,
  requiresChoice: false,
  blocksConnect: false,
  showSurface: false,
  save: () => {},
  reopen: () => {},
  dismiss: () => {},
};

export function useConsent(): ConsentContextValue {
  return useContext(ConsentContext) ?? DETACHED;
}
